import { ReactNode, useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import {
  Banknote,
  BarChart3,
  Boxes,
  Building2,
  CalendarRange,
  FileCog,
  FileSignature,
  BookLock,
  FileText,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Mail,
  Map as MapIcon,
  Menu,
  Package,
  ReceiptText,
  ScrollText,
  Settings as SettingsIcon,
  Users,
  Vault,
  X,
} from "lucide-react";
import clsx from "clsx";
import { useAuth } from "../../context/AuthContext";
import { Logo } from "../ui/Logo";
import { NotificheBell } from "../NotificheBell";
import { ConfinePagina } from "../ErrorBoundary";
import { Role } from "../../types";

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  /**
   * Permesso nominato richiesto per vedere la voce. Se omesso, la voce è visibile
   * a qualunque utente autenticato (es. il Calendario unificato, che non ha un
   * singolo permesso: il backend filtra le righe per tipo sui permessi reali).
   * NB: prima queste voci filtravano su liste di ruoli hardcodate, divergenti
   * da backend e route guard; ora derivano tutte dagli stessi permessi effettivi.
   */
  permission?: string;
}

interface NavGroup {
  /** Intestazione del gruppo. Sparisce quando nessuna voce del gruppo è
   *  visibile per l'utente loggato: un titolo sospeso sul vuoto è peggio di
   *  nessun titolo, e per client/operatori interi gruppi restano vuoti. */
  title: string;
  items: NavItem[];
}

// Il menu è diviso in quattro gruppi per mestiere, non per ordine storico.
// Sopralluoghi, Interventi, Installazioni e Attivazioni NON sono più voci di
// menu: si raggiungono dalle quattro tessere in cima al Calendario, che è la
// pagina dove quei quattro moduli si guardano insieme.
const NAV_GROUPS: NavGroup[] = [
  {
    title: "Supporto",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, permission: "dashboard:read" },
      {
        to: "/calendario",
        label: "Calendario",
        icon: CalendarRange,
        // Vista unificata (installazioni + sopralluoghi + interventi + attivazioni),
        // filtrata lato server sui permessi. Per il cliente è la vista principale
        // sui suoi dati reali; per gli altri è anche il punto d'ingresso ai
        // quattro moduli. Nessun permesso unico: visibile a tutti gli autenticati.
      },
      // Le pratiche dalla richiesta dei documenti all'attivazione (issue #211).
      // Sotto il Calendario perché è lavoro di campo che si segue nel tempo, non
      // anagrafica. Solo admin e staff: `stato_lavori:read` non si concede ad altri.
      { to: "/stato-lavori", label: "Stato lavori", icon: ListChecks, permission: "stato_lavori:read" },
      { to: "/map", label: "Mappa", icon: MapIcon, permission: "mappa:read" },
      { to: "/stats", label: "Statistiche", icon: BarChart3, permission: "statistiche:read" },
    ],
  },
  {
    title: "Gestione Magazzino",
    items: [
      { to: "/catalogo", label: "Catalogo Dispositivi", icon: Package, permission: "catalogo:read" },
      // Vicino al Catalogo perché parlano della stessa cosa, ma sono due livelli
      // diversi: là i MODELLI con la loro documentazione, qui i PEZZI fisici con la
      // loro matricola. Icona cassaforte, non scatola, così la differenza si vede
      // prima di leggere l'etichetta.
      { to: "/devices", label: "Dispositivi", icon: Vault, permission: "devices:read" },
      // Subito dopo Dispositivi perche' e' il gradino prima: qui c'e' quello che e'
      // stato ORDINATO e non e' ancora un pezzo con una matricola. Quando la
      // matricola arriva, la riga di magazzino punta a un device e la scheda vera
      // e' la', non qui.
      { to: "/magazzino", label: "Magazzino", icon: Boxes, permission: "magazzino:read" },
    ],
  },
  {
    title: "Amministrazione",
    items: [
      { to: "/contratti", label: "Contratti", icon: FileSignature, permission: "contratti:read" },
      // Subito sotto Contratti perche' e' cio' che i contratti producono: la riga
      // di registro nasce dalla firma, non da qui.
      { to: "/registro-affari", label: "Registro Affari", icon: BookLock, permission: "registro_affari:read" },  
      // Accanto a Contratti e Registro perche' e' lo stesso mestiere - la parte
      // amministrativa, non quella di campo. Unica delle tre che staff NON vede:
      // `report_ordini:read` e' solo admin (vedi il commento accanto alla chiave in
      // `auth/permissions.ts`).
      { to: "/reports/ordini", label: "Report Ordini", icon: ReceiptText, permission: "report_ordini:read" },
      // Ritiri valori (issue #197): la dashboard, con `ritiri:read` come dice il
      // § 7 della specifica. L'import si apre dal pulsante "Importa" della
      // dashboard, visibile solo con `ritiri:import`.
      { to: "/ritiri-valori", label: "Ritiri valori", icon: Banknote, permission: "ritiri:read" },
      // Sotto le due voci che la usano: il contratto si compila dai dati del
      // cliente e del suo punto vendita.
      { to: "/clients", label: "Anagrafiche", icon: Building2, permission: "clienti_sedi:read" },
    ],
  },
  {
    title: "Impostazioni",
    items: [
      { to: "/settings/users", label: "Utenti", icon: Users, permission: "utenti:read" },
      { to: "/audit-log", label: "Registro attività", icon: ScrollText, permission: "registro_attivita:read" },
      { to: "/settings/email-templates", label: "Template Email", icon: Mail, permission: "email_templates:read" },
      { to: "/settings/tipi-report", label: "Tipi di report", icon: FileCog, permission: "report:configura" },
      { to: "/settings/system", label: "Impostazioni di sistema", icon: SettingsIcon, permission: "impostazioni:gestione" },
    ],
  },
];

// Voci del piede, accanto a "Esci": non sono un modulo di lavoro e non
// appartengono a nessuno dei quattro gruppi.
const FOOTER_ITEMS: NavItem[] = [
  { to: "/privacy", label: "Privacy & Documenti", icon: FileText, permission: "privacy:read" },
];

const ROLE_LABELS: Record<Role, string> = {
  admin: "Admin",
  staff: "Staff Evo",
  centro_assistenza: "Centro Assistenza",
  istituto_vigilanza: "Istituto di Vigilanza",
  operatore_tecnico: "Operatore Tecnico",
  operatore_vigilanza: "Operatore Vigilanza",
  cliente: "Cliente",
};

/** I permessi a cui il backend manda notifiche in app (`notifyUtentiConPermesso`). */
const PERMESSI_CON_NOTIFICHE = ["ritiri:read"];

const NAV_LINK_CLASS =
  "group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150";

export function AppLayout({ children }: { children: ReactNode }) {
  const { user, logout, hasPermission } = useAuth();
  // Sotto lg la sidebar diventa un drawer fuori schermo invece di stare
  // sempre visibile — a 256px fissi non lasciava quasi spazio ai contenuti
  // su mobile (vedi verifica responsive di questa sessione).
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    if (!mobileNavOpen) return;
    document.body.style.overflow = "hidden";
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setMobileNavOpen(false);
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileNavOpen]);

  if (!user) return null;

  // Il menu deriva dai PERMESSI EFFETTIVI dell'utente (default del ruolo +
  // override per-utente), forniti dal backend. Una voce con `permission` appare
  // solo se l'utente ha quel permesso; le voci senza `permission` (Calendario
  // unificato) sono aperte a tutti gli autenticati. Così menu, route guard e
  // backend restano allineati e gli override cambiano davvero cosa si vede.
  const isVisible = (item: NavItem) => !item.permission || hasPermission(item.permission);

  // La campanella la vede chi riceve notifiche: admin e staff (sopralluoghi) e
  // chiunque abbia, per ruolo o per override, un permesso che ne genera. Oggi
  // `ritiri:read`, per "Ritiro non effettuato": il backend le crea per i permessi
  // effettivi, quindi un ruolo diverso con l'override deve poterle vedere e leggere.
  const vedeNotifiche = user.role === "admin" || user.role === "staff" || PERMESSI_CON_NOTIFICHE.some(hasPermission);

  // Un gruppo senza nemmeno una voce visibile sparisce con la sua
  // intestazione: al cliente, per dire, non resta un "AMMINISTRAZIONE" a capo
  // di niente.
  const visibleGroups = NAV_GROUPS.map((group) => ({
    title: group.title,
    items: group.items.filter(isVisible),
  })).filter((group) => group.items.length > 0);

  const visibleFooterItems = FOOTER_ITEMS.filter(isVisible);

  function renderNavLink(item: NavItem) {
    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.to === "/"}
        onClick={() => setMobileNavOpen(false)}
        className={({ isActive }) =>
          clsx(
            NAV_LINK_CLASS,
            isActive ? "bg-white/[0.09] text-white" : "text-white/65 hover:bg-white/5 hover:text-white"
          )
        }
      >
        {({ isActive }) => (
          <>
            {/* Indicatore "sei qui" — stessa idea del rail colorato
                usato per le tessere di sezione, qui in oro per
                restare neutro rispetto al colore del singolo modulo. */}
            <span
              className={clsx(
                "absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r bg-accent transition-opacity duration-150",
                isActive ? "opacity-100" : "opacity-0"
              )}
              aria-hidden="true"
            />
            <item.icon
              className="h-4 w-4 shrink-0 transition-transform duration-150 group-hover:scale-110"
              aria-hidden="true"
            />
            {item.label}
          </>
        )}
      </NavLink>
    );
  }

  return (
    <div className="flex min-h-screen">
      {/* Barra superiore mobile — hamburger + logo, sostituisce la sidebar
          sempre visibile sotto lg */}
      <div className="fixed inset-x-0 top-0 z-header flex items-center justify-between border-b border-line bg-white px-4 py-3 lg:hidden">
        <Link to="/" className="block w-32 active:opacity-80" aria-label="Vai alla Dashboard">
          <Logo className="w-full" />
        </Link>
        <button
          type="button"
          onClick={() => setMobileNavOpen(true)}
          aria-label="Apri il menu"
          className="rounded-lg p-2 text-primary hover:bg-primary/5"
        >
          <Menu className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>

      {/* Overlay del drawer mobile */}
      {mobileNavOpen && (
        <div
          className="fixed inset-0 z-nav-overlay bg-ink/45 backdrop-blur-[2px] lg:hidden"
          aria-hidden="true"
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      {/* Sidebar — drawer fuori schermo su mobile, statica da lg in su.
          z-nav (non z-50) perché sulla pagina Mappa le pane di Leaflet
          arrivano a ~700: il menu aperto veniva coperto dalla mappa da metà
          elenco in giù. Da lg in su torna `static`, quindi lo z-index non
          entra più in gioco. */}
      <aside
        className={clsx(
          "fixed inset-y-0 left-0 z-nav flex w-72 shrink-0 flex-col bg-gradient-to-b from-primary-light via-primary to-primary-dark text-white lg:shadow-[4px_0_24px_-8px_rgba(15,39,61,0.35)]",
          // Da lg il menu sta sopra il contenuto della pagina (z-10 e non z-auto):
          // il pannello delle notifiche esce dal menu verso destra e, a z-auto,
          // finiva sotto le intestazioni `relative` delle pagine.
          "transition-transform duration-200 ease-out lg:static lg:z-10 lg:w-64 lg:translate-x-0",
          // Su telefono l'ombra solo a menu aperto: da chiuso il menu è fuori
          // schermo ma l'ombra sporgeva di ~8px, una striscia scura a sinistra.
          mobileNavOpen ? "translate-x-0 shadow-[4px_0_24px_-8px_rgba(15,39,61,0.35)]" : "-translate-x-full"
        )}
      >
        {/* Fascia bianca a piena larghezza per il logo, separata da una
            riga dorata — il piccolo riquadro bianco di prima limitava
            quanto potesse crescere il logo senza sembrare schiacciato. */}
        <div className="flex items-center justify-between gap-2 bg-white px-6 py-8">
          <Link
            to="/"
            onClick={() => setMobileNavOpen(false)}
            className="block w-full max-w-[210px] active:opacity-80"
            aria-label="Vai alla Dashboard"
          >
            <Logo className="w-full" />
          </Link>
          <button
            type="button"
            onClick={() => setMobileNavOpen(false)}
            aria-label="Chiudi il menu"
            className="shrink-0 rounded p-1 text-ink/40 hover:bg-black/5 hover:text-ink lg:hidden"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <div className="h-[3px] bg-accent" />

        <div className="flex items-center justify-between gap-2 px-5 py-4">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wide text-accent">{ROLE_LABELS[user.role]}</p>
            <p className="truncate text-sm font-semibold text-white">{user.full_name ?? user.email}</p>
          </div>
          {vedeNotifiche && <NotificheBell />}
        </div>

        <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-2">
          {visibleGroups.map((group) => (
            <div key={group.title} className="space-y-1">
              <p className="px-3 pb-1 text-[11px] font-bold uppercase tracking-[0.1em] text-accent">{group.title}</p>
              {group.items.map(renderNavLink)}
            </div>
          ))}
        </nav>

        <div className="space-y-1 border-t border-white/10 px-3 py-4">
          {visibleFooterItems.map(renderNavLink)}
          <button
            type="button"
            onClick={logout}
            className={clsx(NAV_LINK_CLASS, "w-full text-white/65 hover:bg-white/5 hover:text-white")}
          >
            <LogOut className="h-4 w-4 shrink-0" aria-hidden="true" />
            Esci
          </button>
        </div>
      </aside>

      {/* Contenuto */}
      <main className="flex-1 overflow-y-auto bg-surface pt-14 lg:pt-0">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8">
          <ConfinePagina>{children}</ConfinePagina>
        </div>
      </main>
    </div>
  );
}
